// 每日看板 — the owner's single control surface (owner-only, user id 1).
//
// Two feeds on one page:
//  - owner_board_items (/api/owner-board): the cross-session action list that
//    Claude sessions maintain via scripts/oms/owner_board.py.
//  - Claude 對話 (/api/agent-threads): conversations run by the runner on
//    Magnus's PC (workspace scripts/agents/workbench_runner.py) on his own
//    subscription. Each opens at /owner-board/t/:id (AgentThreadPage).
//
// Three tabs (2026-10-16, after Magnus agreed the board left him the hardest job —
// deciding what matters most):
//  - 今日重點: 這週重點 (one recommended priority with two alternatives and the
//    trade-offs, components/board/FocusCard) on top, then everything waiting on him as a
//    compact queue (one line each, tap to open the full card), then the next dated checks.
//  - 90 天方向: the summary strip and the growth map sections below.
//  - 執行與驗證: Claude's conversations, every finished card with how it will be judged
//    (outcome_check / outcome_due), and 最近完成.
// A composer at the bottom starts a new conversation with any model + effort.
//
// The board itself is organised by the growth map (workspace
// docs/plans/growth_map_2026-10-13.html): one section per direction, each with
// its number, its next change-direction check and its own 等你決定 / 進行中 /
// 量測中 boards. At the top: 今天先做 (the one thing to do first: fires, then
// overdue checks, then decisions in map order) and a summary of what waits per
// direction, which directions nobody is working on, and the next dated checks.
// 同意 hands a card to Claude: the backend opens a 對話 thread that the runner on
// Magnus's PC works, and the card shows that run (排隊 / 在做 / 等你看 / 做完了)
// plus Claude's last line. 不同意 can carry a note ("do this instead") that starts
// a run on his version. Every morning the 每日檢查 run (preset morning_brief) checks
// the business, refreshes the headers and the 昨天 strip, and adds the cards.
// The 顧問 run (preset idea_scout) takes one direction a day and may add one
// 顧問點子 card (kind 'idea'): 問題 / 做法 / 為什麼 / 最小測試 + its recommendation.
// 試試看 runs the smallest test; 不要 asks why (his reason trains the next run). A direction with nothing in progress says
// so — the point is to see when the work drifts away from the map. Cards with
// no direction land in 不在地圖上.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PushPinIcon from '@mui/icons-material/PushPin';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import api from '../services/api';
import FocusCard, { FocusRow, FocusStep } from '../components/board/FocusCard';
import {
  AgentPreset,
  AgentThread,
  ApprovalCard,
  Composer,
  Decision,
  Runner,
  WorkstreamTag,
  ago,
  errMessage,
  modelBadge,
  shortDate,
  useAgentConfig,
} from '../components/agent/agentShared';

interface BoardItem {
  id: number;
  lane: 'action' | 'measuring' | 'in_progress';
  title: string;
  detail: string | null;
  impact: string | null;
  due_hint: string | null;
  source: string;
  status: 'open' | 'done' | 'archived';
  done_at: string | null;
  created_at: string;
  updated_at: string | null;
  due_date: string | null;
  readout_key: string | null;
  readout_result: string | null;
  readout_at: string | null;
  direction: DirectionKey | null;
  kind: 'decision' | 'chore' | 'idea' | null;
  recommendation: string | null;
  answer: 'agree' | 'disagree' | 'resolved' | 'done_myself' | null;
  answered_at: string | null;
  snooze_until: string | null;
  thread_id: number | null;
  thread_status: 'queued' | 'running' | 'awaiting_approval' | 'failed' | 'idle' | null;
  thread_status_line: string | null;
  thread_updated_at: string | null;
  thread_result: string | null;
  feed_key?: string | null;
  outcome_check?: string | null;
  outcome_due?: string | null;
  outcome_result?: string | null;
}

type BoardTab = 'today' | 'map' | 'learn';
const TAB_FROM_HASH: Record<string, BoardTab> = { '#map': 'map', '#learn': 'learn' };
const ANSWER_LABEL: Record<string, string> = {
  agree: '你同意',
  disagree: '你不同意',
  resolved: '問題自己消失',
  done_myself: '你自己做了',
}

// One day of the daily check (workspace scripts/oms/daily_business_check.py).
interface Snapshot {
  day: string;
  metrics: {
    storefront_revenue: number;
    storefront_revenue_same_day_last_week: number;
    storefront_orders: number;
    tw_fashion_new_buyers: number;
    returning_revenue: number;
    hk_orders: number;
    counter_sales: number;
    counter_revenue: number;
  };
}
const k = (n: number) => (Math.abs(n) >= 1000 ? `${Math.round(n / 1000)}K` : String(Math.round(n)));

// What Claude's run on a card looks like on the card.
const RUN_CHIP: Record<string, { label: string; color: 'default' | 'primary' | 'warning' | 'error' | 'success' }> = {
  queued: { label: 'Claude 排隊中', color: 'default' },
  running: { label: 'Claude 在做', color: 'primary' },
  awaiting_approval: { label: 'Claude 等你看', color: 'warning' },
  failed: { label: 'Claude 沒跑完', color: 'error' },
  idle: { label: 'Claude 做完了', color: 'success' },
};

type DirectionKey = 'hits' | 'return' | 'new_people' | 'places' | 'money' | 'fires' | 'off_map';

interface DirectionRow {
  key: DirectionKey;
  label: string;
  metric: string | null;
  trend: 'up' | 'down' | 'flat' | null;
  next_check: string | null;
  status: 'on' | 'watch' | 'off' | null;
  verdict: string | null;
  updated_at: string | null;
}

// The growth map's parts, in map order. `map` = one of the engine steps/rules
// that get a section; fires and off_map are listed after them.
const DIRECTION_META: Record<DirectionKey, { label: string; color: string; map: boolean }> = {
  hits: { label: '① 找爆款', color: '#2f6a5a', map: true },
  return: { label: '② 回購', color: '#2f6a5a', map: true },
  new_people: { label: '③ 新客', color: '#1565c0', map: true },
  places: { label: '④ 更多地方', color: '#1565c0', map: true },
  money: { label: '利潤規則', color: '#9a5b12', map: true },
  fires: { label: '救火', color: '#c62828', map: false },
  off_map: { label: '不在地圖上', color: '#757575', map: false },
};
// Fires first (they are the only thing that outranks the map), then the map, then leftovers.
const DIRECTION_ORDER: DirectionKey[] = ['fires', 'hits', 'return', 'new_people', 'places', 'money', 'off_map'];
// Priority of decisions in 今天先做: the map's order, off-map last.
const DIRECTION_RANK: Record<DirectionKey, number> = {
  fires: 0, hits: 1, return: 2, new_people: 3, places: 4, money: 5, off_map: 9,
};
const STATUS_DOT: Record<string, string> = { on: '🟢', watch: '🟠', off: '🔴' };
const dirOf = (it: BoardItem): DirectionKey => it.direction || 'off_map';
const TREND_ARROW: Record<string, string> = { up: '↑', down: '↓', flat: '→' };

// 顧問 ideas carry a real-data preview link (an Artifact page) in their text.
const URL_SPLIT = /(https?:\/\/[^\s）)」，。]+)/;
const firstUrl = (text: string | null | undefined): string | null => (text ? text.match(URL_SPLIT)?.[1] || null : null);
const withLinks = (text: string): React.ReactNode[] =>
  text.split(URL_SPLIT).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
        預覽
      </a>
    ) : (
      part
    )
  );

interface Brief {
  thread_id: number;
  status: string;
  result: string | null;
  updated_at: string;
}

// Whole days elapsed since an ISO timestamp; null when missing/invalid.
const daysSince = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
};

// Calendar day (YYYY-MM-DD) in Taipei for a DATE/timestamp from the API.
const tpeDay = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
};
// Days from today (Taipei) to a date: negative = past, 0 = today, null = no date.
const dayDiff = (iso: string | null | undefined): number | null => {
  const day = tpeDay(iso);
  if (!day) return null;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
  return Math.round((Date.parse(day) - Date.parse(today)) / 86400000);
};
const isSnoozed = (it: BoardItem) => (dayDiff(it.snooze_until) ?? 0) > 0;
const isOverdue = (it: BoardItem) => {
  const d = dayDiff(it.due_date);
  return it.status === 'open' && d !== null && d < 0 && !it.readout_at;
};

const STATUS_ORDER: Record<string, number> = { running: 0, queued: 1, awaiting_approval: 2, failed: 3, idle: 4 };

const SectionTitle: React.FC<{ title: string; count?: number; hint?: string; color?: string }> = ({
  title,
  count,
  hint,
  color,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, mt: 0.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color }}>
      {title}
      {count !== undefined && count > 0 ? ` · ${count}` : ''}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

const OwnerBoardPage: React.FC = () => {
  const navigate = useNavigate();
  const config = useAgentConfig();
  const [items, setItems] = useState<BoardItem[]>([]);
  const [focus, setFocus] = useState<FocusRow | null>(null);
  // 工具頁 (雷達): each tool's headline for the strip on 今日重點 (/owner-board/tools/:key).
  const [tools, setTools] = useState<{ key: string; title: string; direction: string | null; headline: string | null }[]>([]);
  const [tab, setTabState] = useState<BoardTab>(() => TAB_FROM_HASH[window.location.hash] || 'today');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [directions, setDirections] = useState<DirectionRow[]>([]);
  const [dirMenu, setDirMenu] = useState<{ anchor: HTMLElement; item: BoardItem } | null>(null);
  // Per-section open/closed override; unset = the default from sectionDefaultOpen.
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({});
  const [doneOpen, setDoneOpen] = useState(false);
  const [undo, setUndo] = useState<BoardItem | null>(null);
  const [dateEdit, setDateEdit] = useState<number | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  // 不同意 opens a note box on that card: "改成…" starts a run, empty just closes the card.
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [noteText, setNoteText] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [threads, setThreads] = useState<AgentThread[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [runner, setRunner] = useState<Runner | null>(null);
  const [brief, setBrief] = useState<Brief | null>(null);
  const [agentAvailable, setAgentAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [quickAdd, setQuickAdd] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [composer, setComposer] = useState('');
  const [preset, setPreset] = useState<AgentPreset | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [effort, setEffort] = useState<string | null>(null);
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // On a phone the full composer would cover a quarter of the screen: keep it a
  // one-line bar until tapped.
  const [composerOpen, setComposerOpen] = useState(false);
  // 爆款雷達 snapshot (/owner-board/radar): likely hits with no Meta ad show in ① 找爆款.
  const [radar, setRadar] = useState<{
    missing_count: number;
    likely_count: number;
    meta_checked: boolean;
    current: { sid: string; sku: string | null; img: string | null; p: number; in_meta: boolean | null }[];
  } | null>(null);

  const loadBoard = useCallback(async () => {
    try {
      const res = await api.get('/api/owner-board');
      setItems(res.data.items || []);
      setDirections(res.data.directions || []);
      setSnapshots(res.data.snapshots || []);
      setFocus(res.data.focus || null);
      setTools(res.data.radars || []);
    } catch (err) {
      console.error('Failed to load owner board', err);
    }
    try {
      const r = await api.get('/api/owner-board/radar');
      setRadar(r.data.snapshot?.data || null);
    } catch (err) {
      // Backend without the radar yet → the ① section just has no strip.
      setRadar(null);
    }
  }, []);

  const loadAgent = useCallback(async () => {
    try {
      const res = await api.get('/api/agent-threads');
      setThreads(res.data.threads || []);
      setDecisions(res.data.decisions || []);
      setRunner(res.data.runner || null);
      setBrief(res.data.brief || null);
      setAgentAvailable(true);
    } catch (err: any) {
      // Backend without the agent routes yet → board still works on its own.
      if (err?.response?.status === 404) setAgentAvailable(false);
      else console.error('Failed to load conversations', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all([loadBoard(), loadAgent()]);
    setLoading(false);
  }, [loadBoard, loadAgent]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const active = threads.some((t) => t.status === 'running' || t.status === 'queued');
  useEffect(() => {
    const t = setInterval(loadAgent, active ? 3000 : 10000);
    return () => clearInterval(t);
  }, [loadAgent, active]);
  useEffect(() => {
    const t = setInterval(loadBoard, 60000);
    return () => clearInterval(t);
  }, [loadBoard]);

  const curModel = model || config?.defaults.model || 'opus';
  const curEffort = effort || config?.defaults.effort || 'medium';

  // ── board item actions ──
  const toggleDone = async (item: BoardItem) => {
    const next = item.status === 'done' ? 'open' : 'done';
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, status: next } : it)));
    try {
      await api.put(`/api/owner-board/${item.id}`, { status: next });
    } catch (err) {
      console.error('Failed to update item', err);
    }
    loadBoard();
  };

  // Trash = archive (hidden, recoverable) with an undo bar — never a hard delete from a tap.
  const archiveItem = async (item: BoardItem) => {
    setItems((prev) => prev.filter((it) => it.id !== item.id));
    setUndo(item);
    try {
      await api.put(`/api/owner-board/${item.id}`, { status: 'archived' });
    } catch (err) {
      console.error('Failed to archive item', err);
      loadBoard();
    }
  };

  const undoArchive = async () => {
    const item = undo;
    setUndo(null);
    if (!item) return;
    try {
      await api.put(`/api/owner-board/${item.id}`, { status: 'open' });
    } catch (err) {
      console.error('Failed to restore item', err);
    }
    loadBoard();
  };

  // agree → becomes in-progress work; disagree → closed; later → snoozed `days` days.
  const answerItem = async (item: BoardItem, answer: 'agree' | 'disagree' | 'later', days?: number, note?: string) => {
    setNoteFor(null);
    setNoteText('');
    try {
      await api.post(`/api/owner-board/${item.id}/answer`, { answer, days, note: note || undefined });
    } catch (err) {
      setError(errMessage(err));
    }
    loadBoard();
    if (answer === 'agree' || note) loadAgent();
  };

  // Hand an open card to Claude without answering it (e.g. agreed earlier, or a retry).
  const startWork = async (item: BoardItem) => {
    try {
      await api.post(`/api/owner-board/${item.id}/start`);
    } catch (err) {
      setError(errMessage(err));
    }
    loadBoard();
    loadAgent();
  };

  const setDue = async (item: BoardItem, date: string) => {
    setDateEdit(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    try {
      await api.put(`/api/owner-board/${item.id}`, { due_date: date });
    } catch (err) {
      setError(errMessage(err));
    }
    loadBoard();
  };

  const setDirection = async (item: BoardItem, direction: DirectionKey) => {
    setDirMenu(null);
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, direction } : it)));
    try {
      await api.put(`/api/owner-board/${item.id}`, { direction });
    } catch (err) {
      console.error('Failed to set direction', err);
    }
    loadBoard();
  };

  // key = lane, or `${lane}:${direction}` for a direction section's quick-add.
  const addItem = async (key: string) => {
    const title = (quickAdd[key] || '').trim();
    if (!title) return;
    const [lane, direction] = key.split(':');
    setQuickAdd((prev) => ({ ...prev, [key]: '' }));
    try {
      await api.post('/api/owner-board', { lane, title, direction: direction || undefined });
      loadBoard();
    } catch (err) {
      console.error('Failed to add item', err);
    }
  };

  // ── conversation actions ──
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await loadAgent();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const decide = (d: Decision, body: Record<string, unknown>) =>
    act(() => api.post(`/api/agent-threads/${d.thread_id}/decide`, { approval_id: d.id, ...body }));

  const startThread = async (v: { text: string; model: string; effort: string; images: string[] }) => {
    try {
      const res = await api.post('/api/agent-threads', { ...v, preset_key: preset?.key });
      setPreset(null);
      navigate(`/owner-board/t/${res.data.thread.id}`);
    } catch (err) {
      setError(errMessage(err));
      throw err;
    }
  };

  const runPreset = (p: AgentPreset) => {
    if (p.needsInput) {
      setPreset(p);
      return;
    }
    act(async () => {
      const res = await api.post('/api/agent-threads', {
        preset_key: p.key,
        text: composer.trim() || undefined,
        model: curModel,
        effort: curEffort,
      });
      setComposer('');
      navigate(`/owner-board/t/${res.data.thread.id}`);
    });
  };

  // ── derived lists ──
  const open = (lane: string) => items.filter((it) => it.status === 'open' && it.lane === lane);
  const doneItems = items.filter((it) => it.status === 'done');
  const failed = threads.filter((t) => t.status === 'failed');
  const decisionCount = decisions.length + failed.length + open('action').filter((it) => !isSnoozed(it)).length;
  const sortedThreads = useMemo(
    () =>
      [...threads].sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) ||
          (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ),
    [threads]
  );
  const runningCount = threads.filter((t) => t.status === 'running').length;
  const inDir = (lane: string, key: DirectionKey) => open(lane).filter((it) => dirOf(it) === key);
  const liveIn = (key: DirectionKey) => inDir('action', key).filter((it) => !isSnoozed(it)).length;
  const dirRow = (key: DirectionKey) => directions.find((d) => d.key === key);
  const claudeWaiting = decisions.length + failed.length;
  // Runs that finished in the last 24h, so a completed run is never only at the bottom of 對話.
  // Daily presets (每日檢查 / 顧問) report through the brief and their cards instead.
  const recentlyDone = threads.filter(
    (t) =>
      t.status === 'idle' &&
      !['morning_brief', 'idea_scout'].includes(t.preset_key || '') &&
      Date.now() - new Date(t.updated_at).getTime() < 24 * 3600 * 1000
  );

  // Back/forward and #map / #learn links switch tabs too.
  useEffect(() => {
    const onHash = () => setTabState(TAB_FROM_HASH[window.location.hash] || 'today');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const setTab = (t: BoardTab) => {
    setTabState(t);
    window.history.replaceState(window.history.state, '', t === 'today' ? window.location.pathname : `#${t}`);
  };
  const chooseFocus = async (option: 'main' | 'A' | 'B') => {
    if (!focus) return;
    setBusy(true);
    try {
      const res = await api.post(`/api/owner-board/focus/${focus.id}/choose`, { option });
      setFocus(res.data.focus);
      loadBoard();
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };
  // A focus step points at a conversation (its approval waits in the queue) or a card.
  const openStep = (st: FocusStep) => {
    const d = st.thread_id ? decisions.find((x) => x.thread_id === st.thread_id) : null;
    if (d) {
      setOpenRow(`d${d.id}`);
      setTimeout(() => document.getElementById(`q-d${d.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    } else if (st.thread_id) navigate(`/owner-board/t/${st.thread_id}`);
    else if (st.card_id) {
      const it = items.find((x) => x.id === st.card_id);
      if (it) jumpToCard(it);
    }
  };

  const openSection = (key: string) => {
    setTab('map');
    setSectionOpen((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => document.getElementById(`dir-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const jumpToCard = (it: BoardItem) => {
    if (it.status !== 'open') {
      setTab('learn');
      setDoneOpen(true);
    } else setTab('map');
    setSectionOpen((prev) => ({ ...prev, [dirOf(it)]: true }));
    setTimeout(() => document.getElementById(`card-${it.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
  };

  // 今天先做: fires → overdue checks → decisions (map order) → chores. Snoozed cards wait.
  const rankOf = (it: BoardItem): number | null => {
    if (it.status !== 'open' || isSnoozed(it)) return null;
    if (it.lane === 'action' && dirOf(it) === 'fires') return it.kind === 'decision' ? 0.1 : 0;
    if (it.thread_status === 'idle' || it.thread_status === 'awaiting_approval') return 0.5;
    if (it.thread_status === 'failed') return 0.6;
    if (isOverdue(it) && it.lane !== 'in_progress') return 1;
    if (it.lane === 'action' && it.kind === 'decision') return 2 + DIRECTION_RANK[dirOf(it)] / 10;
    if (it.lane === 'action' && it.kind === 'idea') return 2.5 + DIRECTION_RANK[dirOf(it)] / 10;
    if (it.lane === 'action') return 3 + DIRECTION_RANK[dirOf(it)] / 10;
    return null;
  };
  const ranked = items
    .map((it) => ({ it, r: rankOf(it) }))
    .filter((x): x is { it: BoardItem; r: number } => x.r !== null)
    .sort((a, b) => a.r - b.r || a.it.id - b.it.id)
    .map((x) => x.it);
  const hero = ranked[0];
  const nextUp = ranked.slice(1, 3);
  const whyFirst = (it: BoardItem) => {
    if (it.thread_status === 'idle') return 'Claude 做完了';
    if (it.thread_status === 'awaiting_approval') return 'Claude 等你看';
    if (it.thread_status === 'failed') return 'Claude 沒跑完';
    if (dirOf(it) === 'fires') return '救火';
    if (isOverdue(it)) return `逾期 ${-(dayDiff(it.due_date) || 0)} 天`;
    if (it.kind === 'idea') return '顧問點子';
    return it.kind === 'decision' ? '要你決定' : '待你處理';
  };
  // Everything waiting on Magnus, one line each: Claude approvals first (the focus step's
  // own approval at the very top), then failed runs, then cards to answer.
  const focusThreadIds = new Set((focus?.spec.steps || []).map((st) => st.thread_id).filter(Boolean) as number[]);
  const queueDecisions = [...decisions].sort(
    (a, b) => Number(focusThreadIds.has(b.thread_id)) - Number(focusThreadIds.has(a.thread_id))
  );
  const queueCards = open('action')
    .filter((it) => !isSnoozed(it))
    .sort((a, b) => (rankOf(a) ?? 9) - (rankOf(b) ?? 9));
  const snoozedCards = open('action').filter(isSnoozed);
  // 執行與驗證: finished or answered cards and how each will be judged.
  const outcomeRows = items
    // Decisions only: things he answered, card runs Claude finished, or anything with a check.
    // Plain chores closed by a tap are not "did it work?" questions.
    .filter(
      (it) =>
        it.status === 'done' &&
        (it.outcome_check || it.outcome_due || it.answer === 'agree' || it.answer === 'done_myself' || (it.kind === 'idea' && it.answer))
    )
    .sort((a, b) => new Date(b.done_at || 0).getTime() - new Date(a.done_at || 0).getTime());
  const withCheck = outcomeRows.filter((it) => it.outcome_check || it.outcome_due).length;

  const unstaffed = DIRECTION_ORDER.filter((k) => DIRECTION_META[k].map && inDir('in_progress', k).length === 0);
  const upcoming = items
    .filter((it) => it.status === 'open' && (dayDiff(it.due_date) ?? -1) >= 0)
    .sort((a, b) => (dayDiff(a.due_date) ?? 0) - (dayDiff(b.due_date) ?? 0))
    .slice(0, 6);

  const answerButtons = (it: BoardItem, big = false) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
      <Button size={big ? 'medium' : 'small'} variant="contained" disableElevation onClick={() => answerItem(it, 'agree')}>
        同意，開始做
      </Button>
      <Button
        size={big ? 'medium' : 'small'}
        variant={noteFor === it.id ? 'contained' : 'outlined'}
        disableElevation
        color={noteFor === it.id ? 'inherit' : 'primary'}
        onClick={() => {
          setNoteFor(noteFor === it.id ? null : it.id);
          setNoteText('');
        }}
      >
        不同意
      </Button>
      <Button size={big ? 'medium' : 'small'} color="inherit" onClick={() => answerItem(it, 'later', 7)}>
        改天
      </Button>
      {noteFor === it.id && (
        <Box sx={{ width: '100%' }}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            autoFocus
            placeholder="改成…（例：不要查原因，直接換供應商）。留空 = 不做，直接關掉"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => answerItem(it, 'disagree', undefined, noteText.trim())}
            >
              {noteText.trim() ? '照我的改法做' : '不做，關掉'}
            </Button>
            <Button size="small" color="inherit" onClick={() => setNoteFor(null)}>
              取消
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const ideaButtons = (it: BoardItem, big = false) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
      {firstUrl(it.detail) && (
        <Button
          size={big ? 'medium' : 'small'}
          variant="outlined"
          color="secondary"
          component="a"
          href={firstUrl(it.detail) || undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          看預覽
        </Button>
      )}
      <Button size={big ? 'medium' : 'small'} variant="contained" disableElevation color="secondary" onClick={() => answerItem(it, 'agree')}>
        試試看
      </Button>
      <Button
        size={big ? 'medium' : 'small'}
        variant={noteFor === it.id ? 'contained' : 'outlined'}
        disableElevation
        color={noteFor === it.id ? 'inherit' : 'secondary'}
        onClick={() => {
          setNoteFor(noteFor === it.id ? null : it.id);
          setNoteText('');
        }}
      >
        不要
      </Button>
      <Button size={big ? 'medium' : 'small'} color="inherit" onClick={() => answerItem(it, 'later', 14)}>
        之後
      </Button>
      {noteFor === it.id && (
        <Box sx={{ width: '100%' }}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            autoFocus
            placeholder="為什麼不要？（顧問會記住你的理由，下次不再提類似的）"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => answerItem(it, 'disagree', undefined, noteText.trim())}
            >
              送出
            </Button>
            <Button size="small" color="inherit" onClick={() => setNoteFor(null)}>
              取消
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );

  const overdueButtons = (it: BoardItem, big = false) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
      <Button size={big ? 'medium' : 'small'} variant="contained" disableElevation onClick={() => toggleDone(it)}>
        結案
      </Button>
      {dateEdit === it.id ? (
        <TextField
          type="date"
          size="small"
          autoFocus
          onChange={(e) => setDue(it, e.target.value)}
          onBlur={() => setDateEdit(null)}
          sx={{ width: 170 }}
        />
      ) : (
        <Button size={big ? 'medium' : 'small'} variant="outlined" onClick={() => setDateEdit(it.id)}>
          改日期
        </Button>
      )}
    </Stack>
  );

  // A card Claude worked on: open the conversation, close the card, or rerun a failed run.
  const runButtons = (it: BoardItem, big = false) => (
    <Stack direction="row" spacing={1} sx={{ mt: big ? 1.5 : 1, flexWrap: 'wrap', rowGap: 1 }}>
      <Button
        size={big ? 'medium' : 'small'}
        variant="contained"
        disableElevation
        onClick={() => it.thread_id && navigate(`/owner-board/t/${it.thread_id}`)}
      >
        {it.thread_status === 'awaiting_approval' ? '看預覽' : '看結果'}
      </Button>
      {it.thread_status === 'failed' ? (
        <Button size={big ? 'medium' : 'small'} variant="outlined" onClick={() => startWork(it)}>
          重跑
        </Button>
      ) : (
        <Button size={big ? 'medium' : 'small'} variant="outlined" onClick={() => toggleDone(it)}>
          結案
        </Button>
      )}
    </Stack>
  );

  const choreButtons = (it: BoardItem) => (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
      <Button variant="contained" disableElevation onClick={() => toggleDone(it)}>
        完成
      </Button>
      <Button variant="outlined" onClick={() => jumpToCard(it)}>
        看細節
      </Button>
      <Button color="inherit" onClick={() => answerItem(it, 'later', 1)}>
        明天再說
      </Button>
    </Stack>
  );

  const subLane = (label: string, color: string, cards: BoardItem[]) =>
    cards.length === 0 ? null : (
      <Box sx={{ mt: 1.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color, letterSpacing: 0.5 }}>
          {label} · {cards.length}
        </Typography>
        {[...cards].sort((a, b) => Number(isSnoozed(a)) - Number(isSnoozed(b))).map((it) => renderItem(it, false))}
      </Box>
    );

  // ① 找爆款: the radar's likely hits that have no Meta ad yet, one tap from the page.
  const radarStrip = () => {
    if (!radar) return null;
    const missing = radar.current.filter((r) => r.p >= 0.3 && r.in_meta === false);
    return (
      <Box
        onClick={() => navigate('/owner-board/radar')}
        sx={{
          mt: 1,
          p: 1,
          borderRadius: 2,
          bgcolor: missing.length ? '#fdeeea' : 'action.hover',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, color: missing.length ? '#c0392b' : 'text.secondary' }}>
          {missing.length ? `爆款雷達：${missing.length} 款潛力款還沒上 Meta` : '爆款雷達：潛力款都已在 Meta ✓'}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {missing.slice(0, 5).map((r) =>
            r.img ? (
              <Box
                key={r.sid}
                component="img"
                src={r.img}
                alt=""
                loading="lazy"
                sx={{ width: 34, height: 45, objectFit: 'cover', borderRadius: 1, display: 'block' }}
              />
            ) : null
          )}
        </Stack>
        <Typography variant="caption" color="primary" sx={{ ml: 'auto', fontWeight: 600 }}>
          打開雷達 →
        </Typography>
      </Box>
    );
  };

  const renderDirection = (key: DirectionKey) => {
    const meta = DIRECTION_META[key];
    const row = dirRow(key);
    const acts = inDir('action', key);
    const decs = acts.filter((it) => it.kind === 'decision');
    const ideas = acts.filter((it) => it.kind === 'idea');
    const chores = acts.filter((it) => it.kind !== 'decision' && it.kind !== 'idea');
    const prog = inDir('in_progress', key);
    const meas = inDir('measuring', key).sort(
      (a, b) => (dayDiff(a.due_date) ?? 9999) - (dayDiff(b.due_date) ?? 9999)
    );
    if (!meta.map && acts.length + prog.length + meas.length === 0) return null;
    const noWork = meta.map && prog.length === 0;
    const liveActs = acts.filter((it) => !isSnoozed(it)).length;
    const overdueCount = [...acts, ...meas].filter(isOverdue).length;
    // Phone: open only what needs attention, so the page stays short. Desktop: all open.
    const isOpen = sectionOpen[key] ?? (!isMobile || liveActs > 0 || overdueCount > 0);
    return (
      <Paper
        id={`dir-${key}`}
        key={key}
        variant="outlined"
        sx={{
          px: 1.5,
          pt: 1.25,
          pb: 1,
          borderRadius: 3,
          borderTop: `3px solid ${meta.color}`,
          opacity: key === 'off_map' ? 0.85 : 1,
          scrollMarginTop: 72,
        }}
      >
        <Box onClick={() => setSectionOpen((prev) => ({ ...prev, [key]: !isOpen }))} sx={{ cursor: 'pointer' }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: meta.color, fontSize: 18, mr: 'auto' }}>
              {row?.label || meta.label}
            </Typography>
            {liveActs > 0 && <Chip size="small" color="error" label={`${liveActs} 待你`} />}
            {overdueCount > 0 && <Chip size="small" color="error" variant="outlined" label={`${overdueCount} 逾期`} />}
            {noWork && <Chip size="small" variant="outlined" label="沒人在做" sx={{ color: 'error.main', borderColor: 'error.light' }} />}
            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Stack>
          {row?.verdict && (
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
              {row.status ? `${STATUS_DOT[row.status]} ` : ''}
              {row.verdict}
            </Typography>
          )}
          {row?.metric && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {row.metric}
              {row.trend ? ` ${TREND_ARROW[row.trend]}` : ''}
            </Typography>
          )}
          {row?.next_check && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'warning.dark' }}>
              規則：{row.next_check}
            </Typography>
          )}
        </Box>
        {key === 'hits' && radarStrip()}
        <Collapse in={isOpen}>
          {subLane('顧問點子', '#6a1b9a', ideas)}
          {subLane('要你決定', '#d32f2f', decs)}
          {subLane('待你處理', '#ad1457', chores)}
          {subLane('進行中', '#0288d1', prog)}
          {subLane('量測中', '#ed6c02', meas)}
          {quickAddField(`action:${key}`, '＋ 新增待辦（Enter）')}
        </Collapse>
      </Paper>
    );
  };

  // ── renderers ──
  const renderItem = (item: BoardItem, doneLane: boolean) => {
    const isExpanded = !!expanded[`i${item.id}`];
    const staleDays = item.lane === 'in_progress' && item.status !== 'done' ? daysSince(item.updated_at) : null;
    const isStale = staleDays !== null && staleDays > 7;
    const overdue = !doneLane && isOverdue(item);
    const snoozed = !doneLane && isSnoozed(item);
    const isDecision = !doneLane && item.lane === 'action' && item.kind === 'decision';
    const due = item.due_date ? shortDate(item.due_date) : null;
    return (
      <Box
        id={`card-${item.id}`}
        key={`i${item.id}`}
        sx={{ display: 'flex', alignItems: 'flex-start', py: 1, borderBottom: 1, borderColor: 'divider', scrollMarginTop: 72, opacity: snoozed ? 0.55 : 1 }}
      >
        <Checkbox size="small" checked={item.status === 'done'} onChange={() => toggleDone(item)} sx={{ p: 0.5, mr: 0.5, mt: -0.3 }} />
        <Box sx={{ flex: 1, minWidth: 0, opacity: doneLane ? 0.7 : 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, textDecoration: item.status === 'done' ? 'line-through' : 'none', wordBreak: 'break-word' }}
          >
            {item.title}
          </Typography>
          {!doneLane && item.kind === 'idea' && item.lane === 'action' && (
            <Box sx={{ mt: 0.5, pl: 1, borderLeft: '3px solid #ce93d8' }}>
              {item.detail && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}>
                  {withLinks(item.detail)}
                </Typography>
              )}
              {item.recommendation && (
                <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
                    顧問建議
                  </Box>
                  {item.recommendation}
                </Typography>
              )}
              {!snoozed && ideaButtons(item)}
            </Box>
          )}
          {isDecision && item.recommendation && (
            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', wordBreak: 'break-word' }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', mr: 0.75 }}>
                建議
              </Box>
              {item.recommendation}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {item.thread_id && item.thread_status && !doneLane && (
              <Chip
                size="small"
                color={RUN_CHIP[item.thread_status]?.color || 'default'}
                variant={item.thread_status === 'running' ? 'filled' : 'outlined'}
                label={
                  item.thread_status === 'running' && item.thread_status_line
                    ? `Claude 在做：${item.thread_status_line.slice(0, 24)}`
                    : RUN_CHIP[item.thread_status]?.label || item.thread_status
                }
                onClick={() => navigate(`/owner-board/t/${item.thread_id}`)}
              />
            )}
            {!doneLane && item.lane === 'action' && dayDiff(item.created_at) === 0 && (
              <Chip label="今天新" size="small" color="info" />
            )}
            {overdue && <Chip label={`逾期 ${-(dayDiff(item.due_date) || 0)} 天`} size="small" color="error" />}
            {!overdue && due && item.lane === 'measuring' && (
              <Chip label={`⏰ ${due} 讀數`} size="small" color="warning" variant="outlined" />
            )}
            {!due && item.due_hint && <Chip label={item.due_hint} size="small" color="warning" variant="outlined" />}
            {snoozed && <Chip label={`改天 · ${shortDate(item.snooze_until)}`} size="small" variant="outlined" />}
            {isStale && <Chip label={`過期 ${staleDays}天`} size="small" color="error" />}
            {item.impact && <Chip label={item.impact} size="small" color="success" variant="outlined" />}
            {doneLane && item.answer && (
              <Chip label={item.answer === 'agree' ? '你同意' : '你不同意'} size="small" variant="outlined" />
            )}
            {item.source === 'claude' && (
              <Tooltip title="由 Claude session 建立/更新">
                <SmartToyOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.3 }} />
              </Tooltip>
            )}
            {doneLane && item.done_at && <Chip label={`✓ ${shortDate(item.done_at)}`} size="small" variant="outlined" />}
          </Box>
          {item.lane === 'measuring' && item.readout_key && !doneLane && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
              🤖 到期自動讀數
            </Typography>
          )}
          {item.readout_result && (
            <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: '#fff8e1', border: '1px solid', borderColor: 'warning.light' }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'warning.dark' }}>
                📊 讀數結果
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                {item.readout_result}
              </Typography>
            </Box>
          )}
          {!doneLane && item.thread_result && item.thread_status && ['idle', 'awaiting_approval', 'failed'].includes(item.thread_status) && (
            <Box sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
                  Claude
                </Box>
                {item.thread_result.slice(0, 220)}
                {item.thread_result.length > 220 ? '…' : ''}
              </Typography>
            </Box>
          )}
          {isDecision && !snoozed && answerButtons(item)}
          {!doneLane && item.thread_id && item.thread_status && ['idle', 'awaiting_approval', 'failed'].includes(item.thread_status) &&
            runButtons(item)}
          {!doneLane && item.lane === 'in_progress' && !item.thread_id && (
            <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => startWork(item)}>
              交給 Claude 做
            </Button>
          )}
          {overdue && !isDecision && overdueButtons(item)}
          {item.detail && item.kind !== 'idea' && (
            <Collapse in={isExpanded}>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                {item.detail}
              </Typography>
            </Collapse>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {item.detail && item.kind !== 'idea' && (
            <IconButton size="small" onClick={() => setExpanded((prev) => ({ ...prev, [`i${item.id}`]: !isExpanded }))}>
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
          {!doneLane && (
            <Tooltip title="移到別的方向">
              <IconButton size="small" onClick={(e) => setDirMenu({ anchor: e.currentTarget, item })}>
                <SwapHorizRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="移除（可復原）">
            <IconButton size="small" onClick={() => archiveItem(item)}>
              <DeleteOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const quickAddField = (lane: string, placeholder: string) => (
    <TextField
      fullWidth
      size="small"
      variant="standard"
      placeholder={placeholder}
      value={quickAdd[lane] || ''}
      onChange={(e) => setQuickAdd((prev) => ({ ...prev, [lane]: e.target.value }))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') addItem(lane);
      }}
      sx={{ mt: 1 }}
    />
  );

  const statusIcon = (t: AgentThread) => {
    if (t.status === 'running')
      return (
        <AutorenewRoundedIcon
          fontSize="small"
          color="primary"
          sx={{ animation: 'spin 1.6s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }}
        />
      );
    if (t.status === 'queued') return <ScheduleRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
    if (t.status === 'awaiting_approval') return <HelpOutlineRoundedIcon fontSize="small" color="warning" />;
    if (t.status === 'failed') return <ErrorOutlineRoundedIcon fontSize="small" color="error" />;
    return <ChatBubbleOutlineRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  };

  const threadLine = (t: AgentThread) => {
    if (t.status === 'running') return t.status_line || '執行中…';
    if (t.status === 'queued') {
      if (!runner?.online) return '等電腦上線';
      if (runner.auth_ok === false) return '等 Claude 登入';
      return '排隊中';
    }
    if (t.status === 'awaiting_approval') return '等你決定';
    if (t.status === 'failed') return '沒跑完 · 點進去重試';
    const prefix = t.preview_role === 'user' ? '你：' : '';
    return `${prefix}${(t.preview || '').replace(/\s+/g, ' ')}`;
  };

  const renderThread = (t: AgentThread) => (
    <Stack
      key={t.id}
      direction="row"
      spacing={1.25}
      alignItems="center"
      onClick={() => navigate(`/owner-board/t/${t.id}`)}
      sx={{ py: 1.1, borderBottom: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      {statusIcon(t)}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          {t.pinned && <PushPinIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
            {t.title}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {threadLine(t)}
        </Typography>
      </Box>
      <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
        <Box
          component="span"
          sx={{ fontSize: 10.5, px: 0.75, py: 0.1, border: 1, borderColor: 'divider', borderRadius: 1, color: 'text.secondary' }}
        >
          {modelBadge(t.model, t.effort)}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
          {ago(t.status === 'running' ? t.run_started_at : t.updated_at)}
        </Typography>
      </Stack>
    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const briefText =
    brief?.status === 'idle' && brief.result
      ? brief.result
      : brief && ['queued', 'running'].includes(brief.status)
      ? '每日檢查中…'
      : null;

  return (
    <Box sx={{ maxWidth: 1180, width: '100%', mx: 'auto', minWidth: 0, pb: 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* ── header + brief ── */}
      <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            每日看板
          </Typography>
          {agentAvailable && (
            <Tooltip
              title={
                runner
                  ? `最後連線 ${new Date(runner.last_seen_at).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}`
                  : 'workbench_runner.py 尚未啟動'
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={!runner?.online ? 'default' : runner.auth_ok === false ? 'error' : 'success'}
                label={
                  !runner?.online
                    ? '電腦離線'
                    : runner.auth_ok === false
                    ? 'Claude 需要登入'
                    : `電腦在線${runningCount ? ` · ${runningCount} 條在跑` : ''}`
                }
              />
            </Tooltip>
          )}
        </Stack>
        {/* The daily check's brief stays to 3 lines so 這週重點 is on the first screen; tap to read it all. */}
        <Typography
          variant="body2"
          onClick={() => setBriefOpen((v) => !v)}
          sx={{
            mt: 1,
            color: 'text.secondary',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            cursor: 'pointer',
            ...(briefOpen ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}
        >
          {briefText || `${decisionCount ? `有 ${decisionCount} 件等你決定` : '目前沒有要你決定的事'}。`}
        </Typography>
        {brief && briefOpen && (
          <Button size="small" sx={{ px: 0, minWidth: 0 }} onClick={() => navigate(`/owner-board/t/${brief.thread_id}`)}>
            打開每日檢查的對話
          </Button>
        )}
      </Paper>

      {runner?.online && runner.auth_ok === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Claude 的登入過期了，對話先暫停（不會失敗）。電腦上已經開好登入頁，按「授權」後會自動繼續。
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        variant="fullWidth"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', position: 'sticky', top: { xs: 56, sm: 0 }, zIndex: 4, bgcolor: 'background.default' }}
      >
        <Tab value="today" label="今日重點" />
        <Tab value="map" label="90 天方向" />
        <Tab value="learn" label="執行與驗證" />
      </Tabs>

      {tab === 'today' && (
        <>
      {/* Without a focus the old 今天先做 hero leads; with one, only a 救火 card may sit above it. */}
      {(!focus || (hero && dirOf(hero) === 'fires')) && (
        <>
      {/* ── 今天先做: the one thing to do first ── */}
      {!hero && (
        <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, mb: 2, borderRadius: 3, borderLeft: '5px solid #2e7d32' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.main', letterSpacing: 1 }}>
            今天先做
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
            今天沒有要你處理的事 ✓
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {(() => {
              const later = items.filter((it) => it.status === 'open' && isSnoozed(it));
              const back = later.map((it) => tpeDay(it.snooze_until)).filter(Boolean).sort()[0];
              return later.length
                ? `${later.length} 件改天再看（最早 ${shortDate(back)} 回來）。每天早上的每日檢查會再加新的。`
                : '每天早上的每日檢查會看整體生意，有需要你的事就會出現在這裡。';
            })()}
          </Typography>
        </Paper>
      )}
      {hero && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.75, md: 2.25 }, mb: 2, borderRadius: 3, borderLeft: `5px solid ${DIRECTION_META[dirOf(hero)].color}` }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main', letterSpacing: 1 }}>
            今天先做 · {whyFirst(hero)}
            {dirOf(hero) !== 'fires' ? ` · ${DIRECTION_META[dirOf(hero)].label}` : ''}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, fontSize: 19, wordBreak: 'break-word' }}>
            {hero.title}
          </Typography>
          {hero.thread_result && hero.thread_status && ['idle', 'awaiting_approval'].includes(hero.thread_status) ? (
            <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              Claude：{hero.thread_result.slice(0, 220)}
              {hero.thread_result.length > 220 ? '…' : ''}
            </Typography>
          ) : hero.kind === 'idea' ? (
            // A consultant idea is only worth deciding on with its 問題 / 做法 / 為什麼 / 最小測試 in view.
            <Box sx={{ mt: 0.5, pl: 1, borderLeft: '3px solid #ce93d8' }}>
              {hero.detail && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {withLinks(hero.detail)}
                </Typography>
              )}
              {hero.recommendation && (
                <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
                    顧問建議
                  </Box>
                  {hero.recommendation}
                </Typography>
              )}
            </Box>
          ) : (
            (hero.recommendation || hero.detail) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                {hero.recommendation
                  ? `建議：${hero.recommendation}`
                  : `${(hero.detail || '').slice(0, 90)}${(hero.detail || '').length > 90 ? '…' : ''}`}
              </Typography>
            )
          )}
          {hero.thread_id && hero.thread_status && ['idle', 'awaiting_approval', 'failed'].includes(hero.thread_status)
            ? runButtons(hero, true)
            : hero.lane === 'action' && hero.kind === 'idea'
            ? ideaButtons(hero, true)
            : hero.lane === 'action' && hero.kind === 'decision'
            ? answerButtons(hero, true)
            : isOverdue(hero)
            ? overdueButtons(hero, true)
            : choreButtons(hero)}
          {nextUp.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                接著
              </Typography>
              {nextUp.map((it) => (
                <Typography
                  key={it.id}
                  variant="body2"
                  onClick={() => jumpToCard(it)}
                  sx={{ cursor: 'pointer', py: 0.25, '&:hover': { textDecoration: 'underline' } }}
                >
                  <Box component="span" sx={{ color: DIRECTION_META[dirOf(it)].color, fontWeight: 700, mr: 0.75 }}>
                    {DIRECTION_META[dirOf(it)].label}
                  </Box>
                  {it.title}
                  {dirOf(it) !== 'fires' && (
                    <Box component="span" sx={{ color: 'text.secondary', ml: 0.75, fontSize: 12 }}>
                      {whyFirst(it)}
                    </Box>
                  )}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}

        </>
      )}
      {focus && (
        <Box sx={{ mb: 2 }}>
          <FocusCard focus={focus} decisions={decisions} threads={threads} busy={busy} onChoose={chooseFocus} onOpenStep={openStep} />
        </Box>
      )}
      {/* ── 工具: radar-style pages — see the items, tick, one action ── */}
      {(radar || tools.length > 0) && (
      <Box sx={{ mb: 2 }}>
        <SectionTitle title="工具" color="#1f5b4c" hint="看清單、勾選、一鍵執行" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {radar && (
            <Paper variant="outlined" onClick={() => navigate('/owner-board/radar')} sx={{ p: 1.5, borderRadius: 2.5, cursor: 'pointer', minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                爆款雷達 <Box component="span" sx={{ color: DIRECTION_META.hits.color, fontWeight: 500, fontSize: 12 }}>{DIRECTION_META.hits.label}</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(() => {
                  const missing = radar.current.filter((r) => r.p >= 0.3 && r.in_meta === false).length;
                  return missing ? `${missing} 款潛力款還沒上 Meta` : '潛力款都已在 Meta';
                })()}
              </Typography>
            </Paper>
          )}
          {tools.map((t) => {
            const meta = DIRECTION_META[(t.direction as DirectionKey) || 'off_map'] || DIRECTION_META.off_map;
            return (
              <Paper key={t.key} variant="outlined" onClick={() => navigate(`/owner-board/tools/${t.key}`)} sx={{ p: 1.5, borderRadius: 2.5, cursor: 'pointer', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t.title} <Box component="span" sx={{ color: meta.color, fontWeight: 500, fontSize: 12 }}>{meta.label}</Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.headline || '打開看'}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </Box>
      )}

      {/* ── 等你處理: one line per item, tap to open the full card ── */}
      <Box id="claude-decisions" sx={{ mb: 2, scrollMarginTop: 72 }}>
        <SectionTitle title="等你處理" count={queueDecisions.length + failed.length + queueCards.length} color="#d32f2f" hint="點一下打開" />
        {queueDecisions.length + failed.length + queueCards.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            沒有在等你的事 ✓ 每 30 分鐘會再找一次。
          </Typography>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {queueDecisions.map((d) => {
              const key = `d${d.id}`;
              const isOpen = openRow === key;
              const forFocus = focusThreadIds.has(d.thread_id);
              return (
                <Box key={key} id={`q-${key}`} sx={{ borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 }, scrollMarginTop: 72 }}>
                  <Box
                    onClick={() => setOpenRow(isOpen ? null : key)}
                    sx={{ px: 1.75, py: 1.25, cursor: 'pointer', bgcolor: forFocus ? '#e3eee9' : 'transparent', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 0.5, alignItems: 'center' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, wordBreak: 'break-word' }}>
                      {d.meta.summary}
                    </Typography>
                    {forFocus ? (
                      <Chip size="small" label="這週重點的一步" sx={{ bgcolor: '#1f5b4c', color: '#fff', fontWeight: 700 }} />
                    ) : (
                      <WorkstreamTag name={d.workstream} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                      Claude 準備好了 · {ago(d.created_at)}前{d.meta.images?.length ? ` · ${d.meta.images.length} 張圖` : ''}
                      {d.meta.impact ? ` · ${d.meta.impact}` : ''}
                    </Typography>
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ px: 1, pb: 1 }}>
                      <ApprovalCard
                        meta={d.meta}
                        busy={busy}
                        onZoom={setZoomImage}
                        onApprove={(note) => decide(d, { action: 'approve', note })}
                        onAnswer={(answer, note) => decide(d, { action: 'answer', answer, note })}
                        onDone={() => decide(d, { action: 'done' })}
                        onOpen={() => navigate(`/owner-board/t/${d.thread_id}`)}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {failed.map((t) => (
              <Box
                key={`f${t.id}`}
                onClick={() => navigate(`/owner-board/t/${t.id}`)}
                sx={{ px: 1.75, py: 1.25, cursor: 'pointer', borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                  沒跑完：{t.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  點一下看哪裡卡住
                </Typography>
              </Box>
            ))}
            {queueCards.map((it) => {
              const key = `c${it.id}`;
              const isOpen = openRow === key;
              return (
                <Box key={key} sx={{ borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
                  <Box
                    onClick={() => setOpenRow(isOpen ? null : key)}
                    sx={{ px: 1.75, py: 1.25, cursor: 'pointer', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 0.5, alignItems: 'center' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, wordBreak: 'break-word' }}>
                      {it.title}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={DIRECTION_META[dirOf(it)].label}
                      sx={{ color: DIRECTION_META[dirOf(it)].color, borderColor: DIRECTION_META[dirOf(it)].color }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                      {whyFirst(it)}
                    </Typography>
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ px: 1.75, pb: 1.25 }}>
                      {it.recommendation && (
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          建議：{it.recommendation}
                        </Typography>
                      )}
                      {it.thread_id && it.thread_status && ['idle', 'awaiting_approval', 'failed'].includes(it.thread_status)
                        ? runButtons(it)
                        : it.kind === 'idea'
                        ? ideaButtons(it)
                        : it.kind === 'decision'
                        ? answerButtons(it)
                        : isOverdue(it)
                        ? overdueButtons(it)
                        : choreButtons(it)}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Paper>
        )}
        {snoozedCards.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, px: 0.5 }}>
            稍後再看：
            {snoozedCards
              .map((it) => `${it.title.slice(0, 14)}（${shortDate(it.snooze_until)}）`)
              .join(' · ')}
          </Typography>
        )}
      </Box>

      {upcoming.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SectionTitle title="接下來的讀數" color="#9a6200" />
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
            {upcoming.map((it) => (
              <Paper
                key={it.id}
                variant="outlined"
                onClick={() => jumpToCard(it)}
                sx={{ flex: '0 0 auto', px: 1.25, py: 0.75, borderRadius: 2, cursor: 'pointer', maxWidth: 200 }}
              >
                <Typography variant="body2" sx={{ fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontWeight: 500 }}>
                  {shortDate(it.due_date)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                  {it.title}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

        </>
      )}

      {tab === 'map' && (
        <>
      {/* ── summary: decisions per direction · nobody working · next dates ── */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1.25, mb: 2, borderRadius: 3 }}>
        <Stack spacing={1}>
          {snapshots[0] && (() => {
            const m = snapshots[0].metrics;
            const lw = m.storefront_revenue_same_day_last_week;
            const delta = lw ? Math.round(((m.storefront_revenue - lw) / lw) * 100) : null;
            return (
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
                  昨天 {shortDate(snapshots[0].day)}
                </Box>
                官網 NT${k(m.storefront_revenue)}
                {delta !== null && (
                  <Box component="span" sx={{ color: delta >= 0 ? 'success.main' : 'error.main', ml: 0.5 }}>
                    {delta >= 0 ? '↑' : '↓'}
                    {Math.abs(delta)}% vs 上週同日
                  </Box>
                )}
                {' · '}台灣服飾新客 {m.tw_fashion_new_buyers}
                {' · '}回購 NT${k(m.returning_revenue)}
                {' · '}HK {m.hk_orders} 單
                {' · '}櫃位 {m.counter_sales} 筆 NT${k(m.counter_revenue)}
              </Typography>
            );
          })()}
          {recentlyDone.length > 0 && (
            <Stack direction="row" useFlexGap spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', mr: 0.5 }}>
                Claude 做完了
              </Typography>
              {recentlyDone.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  variant="outlined"
                  color="success"
                  label={`${t.title.slice(0, 18)} · ${ago(t.updated_at)}前`}
                  onClick={() => navigate(`/owner-board/t/${t.id}`)}
                  sx={{ maxWidth: '100%' }}
                />
              ))}
            </Stack>
          )}
          {decisionCount > 0 && (
            <Stack direction="row" useFlexGap spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#d32f2f', mr: 0.5 }}>
                等你 {decisionCount} 件
              </Typography>
            {claudeWaiting > 0 && (
              <Chip
                size="small"
                label={`Claude ${claudeWaiting}`}
                onClick={() => setTab('today')}
              />
            )}
            {DIRECTION_ORDER.filter((k) => liveIn(k) > 0).map((k) => (
              <Chip
                key={k}
                size="small"
                variant="outlined"
                label={`${DIRECTION_META[k].label} ${liveIn(k)}`}
                onClick={() => openSection(k)}
                sx={{ color: DIRECTION_META[k].color, borderColor: DIRECTION_META[k].color, fontWeight: 600 }}
              />
            ))}
            </Stack>
          )}
          {unstaffed.length > 0 && (
            <Stack direction="row" useFlexGap spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main', mr: 0.5 }}>
                沒人在做
              </Typography>
              {unstaffed.map((k) => (
                <Chip
                  key={k}
                  size="small"
                  label={DIRECTION_META[k].label}
                  onClick={() => openSection(k)}
                  sx={{ bgcolor: 'transparent', border: 1, borderColor: 'error.light', color: 'error.main' }}
                />
              ))}
            </Stack>
          )}
          {upcoming.length > 0 && (
            <Stack direction="row" useFlexGap spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.dark', mr: 0.5 }}>
                接下來
              </Typography>
              {upcoming.map((it) => (
                <Chip
                  key={it.id}
                  size="small"
                  variant="outlined"
                  label={`${shortDate(it.due_date)} ${it.title}`}
                  onClick={() => jumpToCard(it)}
                  sx={{ maxWidth: '100%' }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* ── the growth map: one section per direction, its boards underneath ── */}
      <Stack spacing={2}>{DIRECTION_ORDER.map(renderDirection)}</Stack>

        </>
      )}

      {tab === 'learn' && (
        <>
      {/* ── Claude conversations ── */}
      {agentAvailable && (
        <Box sx={{ mb: 2 }}>
          <SectionTitle title="對話" count={threads.filter((t) => t.status !== 'idle').length} color="#0288d1" />
          <Paper variant="outlined" sx={{ px: 1.5, borderRadius: 3 }}>
            {sortedThreads.map(renderThread)}
            {threads.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                還沒有對話，在下面交代 Claude 一件事。
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* ── 執行與驗證: finished cards and how each will be judged ── */}
      <Box sx={{ mb: 2 }}>
        <SectionTitle
          title="做完了，有效嗎？"
          count={outcomeRows.length}
          color="#1f5b4c"
          hint={outcomeRows.length ? `${withCheck} 件有讀數方法` : undefined}
        />
        {outcomeRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            14 天內還沒有完成的決定。
          </Typography>
        ) : (
          // Stacked rows, not a table: four columns do not fit a phone.
          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {outcomeRows.map((it) => (
              <Box
                key={it.id}
                onClick={() => jumpToCard(it)}
                sx={{ px: 1.75, py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 }, cursor: 'pointer', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 0.5, alignItems: 'start' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, wordBreak: 'break-word' }}>
                  <Box component="span" sx={{ color: DIRECTION_META[dirOf(it)].color, fontWeight: 700, mr: 0.75 }}>
                    {DIRECTION_META[dirOf(it)].label}
                  </Box>
                  {it.title}
                </Typography>
                {it.outcome_result ? (
                  <Chip size="small" label={it.outcome_result.slice(0, 24)} sx={{ bgcolor: '#e5f3ea', color: '#1d7a45', fontWeight: 700, maxWidth: 150 }} />
                ) : it.outcome_due ? (
                  <Chip size="small" variant="outlined" label={`${shortDate(it.outcome_due)} 讀數`} />
                ) : (
                  <Chip size="small" label="還沒定" sx={{ bgcolor: '#fbf1dc', color: '#9a6200', fontWeight: 700 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', lineHeight: 1.45 }}>
                  {(it.answer && ANSWER_LABEL[it.answer]) || '完成'} {shortDate(it.done_at)}
                  {it.outcome_check ? ` · 看：${it.outcome_check}` : ''}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, px: 0.5 }}>
          做完不等於有效。Claude 完成一張卡時會寫下看哪個數字、哪天讀；週日檢討會用這張表。
        </Typography>
      </Box>

      {/* ── recently done ── */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mt: 2, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" onClick={() => setDoneOpen((v) => !v)} sx={{ cursor: 'pointer' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32', flex: 1 }}>
            最近完成{doneItems.length ? ` · ${doneItems.length}` : ''}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              14 天
            </Typography>
          </Typography>
          {doneOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Stack>
        <Collapse in={doneOpen}>
          {doneItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              還沒有。
            </Typography>
          )}
          {doneItems.map((it) => renderItem(it, true))}
        </Collapse>
      </Paper>

        </>
      )}

      <Snackbar
        open={!!undo}
        autoHideDuration={6000}
        onClose={() => setUndo(null)}
        message={undo ? `已移除「${undo.title}」` : ''}
        action={
          <Button color="secondary" size="small" onClick={undoArchive}>
            復原
          </Button>
        }
      />

      <Menu anchorEl={dirMenu?.anchor} open={!!dirMenu} onClose={() => setDirMenu(null)}>
        {DIRECTION_ORDER.map((k) => (
          <MenuItem
            key={k}
            selected={!!dirMenu && dirOf(dirMenu.item) === k}
            onClick={() => dirMenu && setDirection(dirMenu.item, k)}
            sx={{ color: DIRECTION_META[k].color, fontSize: 14 }}
          >
            {DIRECTION_META[k].label}
          </MenuItem>
        ))}
      </Menu>

      {/* ── composer: start a conversation ── */}
      {agentAvailable && !composerOpen && (
        <Paper
          elevation={6}
          onClick={() => setComposerOpen(true)}
          sx={{
            position: 'sticky',
            bottom: { xs: 8, md: 16 },
            mt: 3,
            zIndex: 5,
            borderRadius: 6,
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'text',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>
            {runner?.online ? '開新對話，交代 Claude 一件事…' : '開新對話…（電腦上線後開始）'}
          </Typography>
          <Box component="span" sx={{ fontSize: 11, color: 'text.secondary', border: 1, borderColor: 'divider', borderRadius: 1, px: 0.75 }}>
            {modelBadge(curModel, curEffort)}
          </Box>
        </Paper>
      )}
      {agentAvailable && composerOpen && (
        <Box sx={{ position: 'sticky', bottom: { xs: 8, md: 16 }, mt: 3, zIndex: 5 }}>
          <Stack direction="row" useFlexGap spacing={0.75} sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}>
            {(config?.presets || []).map((p) => (
              <Chip
                key={p.key}
                label={p.needsInput ? `${p.label}…` : p.label}
                size="small"
                color={preset?.key === p.key ? 'primary' : 'default'}
                variant={preset?.key === p.key ? 'filled' : 'outlined'}
                disabled={busy}
                onClick={() => (preset?.key === p.key ? setPreset(null) : runPreset(p))}
                sx={{ bgcolor: preset?.key === p.key ? undefined : 'background.paper' }}
              />
            ))}
          </Stack>
          <Composer
            config={config}
            model={curModel}
            effort={curEffort}
            onModelChange={setModel}
            onEffortChange={setEffort}
            text={composer}
            onTextChange={setComposer}
            onSubmit={startThread}
            autoFocus
            extraLeft={
              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() => {
                  setComposerOpen(false);
                  setPreset(null);
                }}
                sx={{ ml: 'auto', cursor: 'pointer' }}
              >
                收起
              </Typography>
            }
            placeholder={
              preset
                ? `${preset.label}：${preset.placeholder || '補充說明'}`
                : runner?.online
                ? '開新對話，交代 Claude 一件事…'
                : '開新對話…（電腦上線後開始）'
            }
          />
        </Box>
      )}

      <Dialog open={!!zoomImage} onClose={() => setZoomImage(null)} maxWidth="md">
        {zoomImage && <Box component="img" src={zoomImage} alt="preview" sx={{ width: '100%', display: 'block' }} />}
      </Dialog>
    </Box>
  );
};

export default OwnerBoardPage;
